import { acceptHMRUpdate, defineStore } from 'pinia';
import type {
  AssignCompareListToCustomerMutation,
  CommonProductFragment,
  GetCompareListQuery
} from '~~/lib/Shop/generated/schema';
import { useCompareListCookie } from '~/composables/useCompareListCookie';
import { useCustomerAccountStore } from '~/stores/customerAccountStore/customerAccountStore';
import { extractError } from '~/utils/extractError';
import { leadingTrailingDebounce } from '~/composables/useDebounce';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { useLogger } from '~/composables/useLogger';

export type CompareListData = NonNullable<GetCompareListQuery['compareList']>;

type CompareItem = NonNullable<CompareListData['items']>[number];

interface CompareItemState {
  product: NonNullable<CompareItem>['product'];
  uid: string;
  attributes: NonNullable<CompareItem>['attributes'];
  /** Needs to be added to Magento via PUT. */
  new: boolean;
  /**
   * Marked for deletion by the user. Item is hidden from the UI immediately.
   * When `deleteSent` is also true the DELETE has already been dispatched
   * and the item stays in localItems until a Magento GET confirms it is gone.
   */
  deleted: boolean;
  /**
   * DELETE has been sent to Magento and received a 200 response, but Magento's
   * eventual consistency means the item may still appear in subsequent GET
   * responses for up to 20+ seconds. The item is kept in localItems with this
   * flag set so mergeFromMagento() knows NOT to re-add it, and so a second
   * DELETE is never sent.
   */
  deleteSent: boolean;
  retryCount: number;
  syncError?: string;
  /**
   * True while `enrichProductInBackground` is in flight for this item.
   * Set to false once enrichment completes (success or error) so UI can
   * switch from a loading skeleton to the real product header.
   */
  isEnriching: boolean;
}

interface PendingChanges {
  toAdd: string[];
  toRemove: string[];
}

interface CompareListAction {
  type: 'addProduct' | 'removeProduct';
  success?: boolean;
  error?: string;
  productName?: string;
}

/**
 * Safely decodes a base64-encoded string without throwing.
 */
function safeAtob(value: string): string {
  try {
    return atob(value);
  } catch {
    return value;
  }
}

/**
 * Returns true when the error from a $fetch call is an HTTP 404,
 * which the compare list server routes return for a stale/expired list UID.
 */
function isListNotFoundError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    (error as { statusCode: unknown }).statusCode === 404
  );
}

/**
 * Compare list state and Magento compare-list operations (guest + customer).
 *
 * `localItems` is the authoritative source of truth for the UI.
 * Magento is a background persistence backend — its responses never remove items
 * from local state due to its eventual consistency guarantees.
 */
export const useCompareListStore = defineStore('shop-compare-list', () => {
  const { $shopStore, $locale, $analytics } = useNuxtApp();
  const { isAuthenticated } = useShopAuth();
  const customerAccountStore = useCustomerAccountStore();
  const logger = useLogger();

  const localItems = ref<CompareItemState[]>([]);
  const loading = ref(false);
  const lastError = ref<string | undefined>();
  const isMergingGuestList = ref(false);
  const syncing = ref(false);
  const maxRetries = 3;
  const currentAction = ref<CompareListAction | null>(null);
  const lastAction = ref<CompareListAction | null>(null);

  function clearListCookie(): void {
    const storeKey = $shopStore.value;
    const c = useCompareListCookie($locale.value);
    c.value = { ...c.value, [storeKey]: undefined };
  }

  const items = computed(() =>
    localItems.value.filter((item) => !item.deleted)
  );

  const itemCount = computed(() => items.value.length);

  const activeListUid = computed((): string | undefined => {
    const storeKey = $shopStore.value;

    // During merge, always trust the cookie as source of truth.
    // This prevents race conditions with customerData updates.
    if (isMergingGuestList.value) {
      return useCompareListCookie($locale.value).value[storeKey] ?? undefined;
    }

    if (isAuthenticated.value) {
      const fromCustomer = customerAccountStore.customerData?.compare_list?.uid;
      if (fromCustomer) {
        return fromCustomer;
      }
    }
    return useCompareListCookie($locale.value).value[storeKey] ?? undefined;
  });

  /**
   * Checks whether a product is in the visible list.
   * Compares by SKU, UID, or decoded (base64) UID — no network call.
   */
  function hasProduct(skuOrUid: string): boolean {
    return items.value.some((row) => {
      if (!row?.product?.uid) {
        return false;
      }
      if (row.product.sku === skuOrUid || row.product.uid === skuOrUid) {
        return true;
      }
      return safeAtob(row.product.uid) === skuOrUid;
    });
  }

  /**
   * Merges a Magento compare list response into localItems.
   *
   * - Cold start (empty localItems): populate entirely from Magento.
   * - Otherwise (additive merge):
   *   - Items in Magento but absent locally are added — UNLESS they are already
   *     tracked as deleted/deleteSent (would cause a "reappearance" bug).
   *   - Existing non-new, non-deleted items are enriched with full Magento data
   *     (attribute_set, custom_attributesV2, etc.) for the specs table.
   *   - Items with `deleteSent=true` that are no longer present in the Magento
   *     response are confirmed gone and removed from localItems.
   *
   * Items already in localItems are NEVER removed solely because Magento's
   * (potentially stale) GET response does not include them.
   */
  function mergeFromMagento(data: CompareListData): void {
    if (!data.items) {
      return;
    }

    const magentoItems = data.items.filter(
      Boolean
    ) as NonNullable<CompareItem>[];

    if (localItems.value.length === 0) {
      // Cold start: populate entirely from Magento.
      localItems.value = magentoItems.map((item) => ({
        product: item.product,
        uid: item.uid,
        attributes: item.attributes,
        new: false,
        deleted: false,
        deleteSent: false,
        retryCount: 0,
        isEnriching: false
      }));
      return;
    }

    const magentoProductUids = new Set(magentoItems.map((i) => i.product.uid));

    const localUidMap = new Map<string, CompareItemState>(
      localItems.value.map((item) => [item.product.uid, item])
    );

    for (const magentoItem of magentoItems) {
      const existing = localUidMap.get(magentoItem.product.uid);
      if (!existing) {
        // Present in Magento but not locally → add it.
        localItems.value.push({
          product: magentoItem.product,
          uid: magentoItem.uid,
          attributes: magentoItem.attributes,
          new: false,
          deleted: false,
          deleteSent: false,
          retryCount: 0,
          isEnriching: false
        });
      } else if (existing.deleted || existing.deleteSent) {
        // User has deleted (or is in the process of deleting) this item.
        // Do NOT re-add it from the Magento response — Magento is stale.
      } else if (!existing.new) {
        // Enrich with full Magento product data (attribute_set, custom_attributesV2, etc.)
        // Only update non-new items: new items have stub data Magento doesn't know about yet.
        // Also clear isEnriching: Magento's compare list response contains fully enriched data,
        // so background enrichment is no longer needed for this item.
        existing.product = magentoItem.product;
        existing.uid = magentoItem.uid;
        existing.attributes = magentoItem.attributes;
        existing.isEnriching = false;
      }
    }

    // Confirm deletions: items with deleteSent=true that are no longer in the
    // Magento response have been successfully removed — drop them from localItems.
    localItems.value = localItems.value.filter(
      (item) => !(item.deleteSent && !magentoProductUids.has(item.product.uid))
    );
  }

  /**
   * Fetches a compare list from Magento by UID.
   * This is a low-level helper — use refresh() for the standard load flow.
   */
  async function fetchCompareList(
    uid: string
  ): Promise<CompareListData | null> {
    return $fetch<CompareListData | null>('/api/magento/compare', {
      method: 'GET',
      query: {
        store: $shopStore.value,
        locale: $locale.value,
        uid
      }
    });
  }

  let refreshInFlight: Promise<void> | null = null;

  /**
   * Loads / enriches the compare list from Magento (additive merge).
   * Does NOT call syncToMagento() first — that is handled in the background
   * by debouncedSync. This ensures Magento's stale responses never drop local items.
   *
   * Concurrent callers share one in-flight request so a slower/error response
   * cannot wipe items merged by an earlier successful refresh.
   * Callers: compareListAuth plugin (initial boot), PageHeader onMounted (safety-net
   * for late $shopStore resolution), and the onLoggedIn() retry loop.
   */
  async function refresh(): Promise<void> {
    if (refreshInFlight) {
      return refreshInFlight;
    }

    refreshInFlight = (async () => {
      if (isAuthenticated.value && !customerAccountStore.customerData) {
        await customerAccountStore.loadCustomer();
      }

      const uid = activeListUid.value;
      if (!uid) {
        // No list UID yet — do not wipe localItems. The user may have added items
        // locally (new=true) while ensureListUid() is still pending inside debouncedSync.
        // onLoggedOut() is responsible for clearing localItems on logout.
        return;
      }

      loading.value = true;
      lastError.value = undefined;

      try {
        const data = await fetchCompareList(uid);

        if (data) {
          mergeFromMagento(data);
        } else if (!isAuthenticated.value) {
          // Server returned null → the compare list no longer exists in Magento.
          // Clear the stale cookie so the next addProduct() creates a fresh list.
          // Do NOT wipe localItems — the user may have added items via addProduct()
          // that haven't synced yet (new=true). Those stay until the next sync cycle.
          clearListCookie();
        }
      } catch (e) {
        lastError.value = extractError(e);

        // For guest users the cookie is the only reference to the Magento list.
        // Any Magento error on a cold start means the UID is unrecoverable —
        // clear it so subsequent writes create a new list instead of retrying
        // forever with a dead UID.
        // Do NOT wipe localItems — same reasoning as the success path above.
        if (!isAuthenticated.value) {
          clearListCookie();
        }
      } finally {
        loading.value = false;
      }
    })();

    try {
      await refreshInFlight;
    } finally {
      refreshInFlight = null;
    }
  }

  /**
   * Ensures a compare list exists and returns its uid (creates one if needed).
   */
  async function ensureListUid(): Promise<string> {
    if (isAuthenticated.value && !customerAccountStore.customerData) {
      await customerAccountStore.loadCustomer();
    }

    const existing = activeListUid.value;
    if (existing) {
      return existing;
    }

    loading.value = true;
    lastError.value = undefined;

    try {
      const created = await $fetch<CompareListData | null>(
        '/api/magento/compare',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value
          },
          body: {}
        }
      );

      if (!created?.uid) {
        throw new Error('Compare list could not be created');
      }

      const storeKey = $shopStore.value;
      const c = useCompareListCookie($locale.value);
      c.value = { ...c.value, [storeKey]: created.uid };

      return created.uid;
    } catch (e) {
      lastError.value = extractError(e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function getPendingChanges(): PendingChanges {
    const toAdd: string[] = [];
    const toRemove: string[] = [];

    for (const item of localItems.value) {
      // Ephemeral: added then deleted before any sync — nothing to send
      if (item.new && item.deleted) {
        continue;
      }

      if (item.new && !item.deleted) {
        toAdd.push(safeAtob(item.product.uid));
      } else if (item.deleted && !item.new && !item.deleteSent) {
        // Only queue DELETE if it has not been sent yet.
        // deleteSent=true means the 200 came back but Magento is still stale —
        // do not send a duplicate DELETE.
        toRemove.push(safeAtob(item.product.uid));
      }
    }

    return { toAdd, toRemove };
  }

  async function syncAdditions(
    listUid: string,
    productIds: string[]
  ): Promise<void> {
    await $fetch<CompareListData | null>('/api/magento/compare/items', {
      method: 'PUT',
      query: {
        store: $shopStore.value,
        locale: $locale.value
      },
      body: {
        listUid,
        productUids: productIds
      }
    });

    // Clear 'new' flags for successfully added items
    productIds.forEach((id) => {
      const item = localItems.value.find((i) => safeAtob(i.product.uid) === id);
      if (item) {
        item.new = false;
        item.retryCount = 0;
        item.syncError = undefined;
      }
    });
  }

  async function syncDeletions(
    listUid: string,
    productIds: string[]
  ): Promise<void> {
    await $fetch<CompareListData | null>('/api/magento/compare/items', {
      method: 'DELETE',
      query: {
        store: $shopStore.value,
        locale: $locale.value
      },
      body: {
        listUid,
        productUids: productIds
      }
    });

    // Magento returned 200 but the item may still appear in GET responses for
    // 20+ seconds (eventual consistency). Mark deleteSent=true so:
    // 1. mergeFromMagento() will not re-add these items from stale GET responses.
    // 2. getPendingChanges() will not queue a duplicate DELETE.
    // The items are removed from localItems only when a subsequent GET confirms
    // they are no longer present (handled in mergeFromMagento).
    productIds.forEach((id) => {
      const item = localItems.value.find((i) => safeAtob(i.product.uid) === id);
      if (item) {
        item.deleteSent = true;
      }
    });
  }

  function handleSyncError(error: unknown): void {
    const errorMsg = extractError(error);

    // Mark items for retry or permanent error.
    // deleteSent items are excluded: their DELETE was already accepted by Magento
    // (200 OK) so there is nothing to retry.
    localItems.value.forEach((item) => {
      const needsRetry =
        (item.new && !item.deleted) ||
        (item.deleted && !item.new && !item.deleteSent);
      if (needsRetry) {
        if (item.retryCount < maxRetries) {
          item.retryCount++;
          debouncedSync();
        } else {
          item.syncError = errorMsg;
          lastError.value = `Failed to sync after ${maxRetries} attempts: ${errorMsg}`;
        }
      }
    });
  }

  async function syncToMagento(): Promise<void> {
    if (isAuthenticated.value && !customerAccountStore.customerData) {
      await customerAccountStore.loadCustomer();
    }

    const changes = getPendingChanges();

    if (changes.toAdd.length === 0 && changes.toRemove.length === 0) {
      // Clean up items that are both new and deleted (added then immediately removed)
      localItems.value = localItems.value.filter(
        (item) => !(item.new && item.deleted)
      );
      return;
    }

    if (syncing.value) {
      return;
    }

    syncing.value = true;

    try {
      // Lazy list creation: only create a Magento list when there is something to sync.
      const listUid = await ensureListUid();

      try {
        if (changes.toAdd.length > 0) {
          await syncAdditions(listUid, changes.toAdd);
        }

        if (changes.toRemove.length > 0) {
          await syncDeletions(listUid, changes.toRemove);
        }
      } catch (syncError) {
        // For guest users: a 404 from the server means the list UID is stale.
        // Clear the cookie so the next ensureListUid() call (on retry) creates
        // a fresh list. Pending new=true items remain in localItems and will be
        // synced to the new list after handleSyncError() schedules a retry.
        if (!isAuthenticated.value && isListNotFoundError(syncError)) {
          clearListCookie();
        }
        throw syncError;
      }

      // Only remove ephemeral items (added then deleted before any sync reached Magento).
      // deleted && !new items now carry deleteSent=true after syncDeletions() and must
      // stay in localItems until mergeFromMagento() confirms Magento no longer shows them.
      localItems.value = localItems.value.filter(
        (item) => !(item.new && item.deleted)
      );
    } catch (e) {
      handleSyncError(e);
    } finally {
      syncing.value = false;
    }
  }

  // Leading+trailing debounce: fires immediately on first call, then fires once more
  // after 2 s of silence to flush any changes that arrived after the leading fire.
  const debouncedSync = leadingTrailingDebounce(() => {
    syncToMagento();
  }, 2000);

  function pushComparisonListAddEvent(product: Record<string, unknown>): void {
    if (import.meta.server || !$analytics) {
      return;
    }
    $analytics.pushToDataLayer({
      event: 'comparisonlist',
      eventInfo: {
        action: 'addtocomparison',
        category: product.attribute_set_name,
        label:
          (typeof product.name === 'string' ? product.name : '') +
          (typeof product.sku === 'string' ? ' | ' + product.sku : '')
      }
    });
  }

  /**
   * Enriches a locally added product in the background using the full product detail
   * endpoint, which returns `is_visible_on_front: true` custom attributes (brand,
   * camera_series, product_image, etc.) and the full `attribute_set` object.
   *
   * The listing endpoint used in addProduct() only returns `used_in_product_listing`
   * attributes, which does not include brand or series code. Without enrichment the
   * header widget only shows the product name, not the manufacturer or series label.
   * This background fetch resolves the widget data without any user interaction.
   */
  async function enrichProductInBackground(
    urlKey: string,
    productUid: string,
    options?: { trackComparisonListAdd?: boolean }
  ): Promise<void> {
    const trackComparisonListAdd = options?.trackComparisonListAdd ?? false;

    const finishTracking = (item: CompareItemState | undefined) => {
      if (!trackComparisonListAdd || !item || item.deleted) {
        return;
      }
      pushComparisonListAddEvent(item.product as Record<string, unknown>);
    };

    try {
      const enriched = await $fetch<Record<string, unknown>>(
        '/api/magento/product',
        {
          method: 'GET',
          query: {
            urlKey,
            store: $shopStore.value,
            locale: $locale.value
          }
        }
      );

      // Update the item's product data — only if the item is still present and not deleted.
      // Overwrites the listing-level stub with full is_visible_on_front data so the widget
      // can immediately display brand, series label, and other PDP-level attributes.
      // isEnriching is always cleared regardless of whether enriched data was returned,
      // so the header never gets stuck showing a skeleton.
      const item = localItems.value.find(
        (i) => i.product.uid === productUid && !i.deleted
      );
      if (item) {
        if (enriched) {
          item.product = enriched as any;
        }
        item.isEnriching = false;
        finishTracking(item);
      }
    } catch {
      // Non-fatal — the item is still visible with stub data; enrichment can
      // retry on the next refresh() call once the compare list sync propagates.
      const item = localItems.value.find(
        (i) => i.product.uid === productUid && !i.deleted
      );
      if (item) {
        item.isEnriching = false;
        finishTracking(item);
      }
    }
  }

  async function addProduct(skuOrUid: string): Promise<void> {
    const action: CompareListAction = { type: 'addProduct' };
    currentAction.value = action;

    loading.value = true;
    lastError.value = undefined;

    try {
      // Fetch listing-level product data to get uid/sku/url_key for the stub entry.
      // Note: custom_attributesV2 here is filtered to used_in_product_listing only —
      // brand, camera_series, etc. are absent. enrichProductInBackground() fixes this.
      // ensureListUid() is NOT called here; syncToMagento() handles lazy list creation.
      const response = await $fetch<{
        items?: Array<{
          uid: string;
          sku?: string | null;
          name?: string | null;
          url_key?: string | null;
        } | null> | null;
      }>('/api/magento/products', {
        method: 'GET',
        query: {
          store: $shopStore.value,
          locale: $locale.value,
          skus: skuOrUid,
          filters: JSON.stringify({})
        }
      });

      const product = response.items?.[0];
      if (!product) {
        throw new Error(`Product with SKU ${skuOrUid} not found`);
      }

      // Check if already tracked (including items pending deletion)
      const existing = localItems.value.find(
        (item) => item.product.uid === product.uid
      );

      let trackComparisonListAdd = false;

      if (existing) {
        if (existing.deleteSent) {
          // DELETE was already sent to Magento. Re-adding requires a new PUT
          // because Magento may have already processed the removal.
          existing.new = true;
          existing.deleted = false;
          existing.deleteSent = false;
          existing.retryCount = 0;
          existing.syncError = undefined;
          trackComparisonListAdd = true;
        } else if (existing.deleted) {
          // DELETE not yet sent — simply un-mark deletion, no Magento call needed.
          existing.deleted = false;
          trackComparisonListAdd = true;
        }
        // else: already active in list, no action needed
      } else {
        localItems.value.push({
          product: product as any,
          uid: `local-${Date.now()}`,
          attributes: [],
          new: true,
          deleted: false,
          deleteSent: false,
          retryCount: 0,
          isEnriching: !!product.url_key
        });
        trackComparisonListAdd = true;
      }

      // Trigger background sync — no blocking Magento call in this hot path
      debouncedSync();

      // Fire-and-forget background enrichment so the widget shows brand/series immediately.
      // Uses the product detail endpoint which returns is_visible_on_front attributes.
      // Analytics: defer add_to_comparison until enrichment completes (categories from PDP query).
      if (product.url_key) {
        enrichProductInBackground(product.url_key, product.uid, {
          trackComparisonListAdd
        });
      } else if (trackComparisonListAdd) {
        pushComparisonListAddEvent(product as Record<string, unknown>);
      }

      action.productName = product.name ?? product.sku ?? skuOrUid;
      action.success = true;
    } catch (e) {
      action.error = extractError(e);
      action.success = false;
      lastError.value = action.error;
      throw e;
    } finally {
      loading.value = false;
      lastAction.value = action;
      currentAction.value = null;
    }
  }

  async function removeProduct(skuOrUid: string): Promise<void> {
    const action: CompareListAction = { type: 'removeProduct' };
    currentAction.value = action;
    lastError.value = undefined;

    try {
      // Look up directly in localItems — no network round-trip needed.
      // Items carry product.sku and product.uid from when they were added.
      const item = localItems.value.find((i) => {
        if (!i.product?.uid) {
          return false;
        }
        if (i.product.sku === skuOrUid || i.product.uid === skuOrUid) {
          return true;
        }
        return safeAtob(i.product.uid) === skuOrUid;
      });

      // Only act if the item is not already being deleted.
      // deleted=true covers both "pending DELETE" and "DELETE sent (deleteSent=true)".
      if (item && !item.deleted) {
        item.deleted = true;
        debouncedSync();
      }

      action.productName =
        item?.product?.name ?? item?.product?.sku ?? skuOrUid;
      action.success = true;
    } catch (e) {
      action.error = extractError(e);
      action.success = false;
      lastError.value = action.error;
      throw e;
    } finally {
      lastAction.value = action;
      currentAction.value = null;
    }
  }

  /**
   * Merges the current store items (guest snapshot) with the customer's Magento
   * compare list after login. The store is the source of truth:
   *
   * 1. Snapshot non-deleted localItems (guest items to preserve).
   * 2. Attempt Magento list assignment (best-effort).
   * 3. Fetch the customer's (now merged) Magento list.
   * 4. Result = customer Magento items + guest-only items (marked new for sync).
   */
  async function mergeGuestCompareListAfterLogin(): Promise<void> {
    const storeKey = $shopStore.value;

    // Snapshot non-deleted local items before any Magento mutations
    const guestSnapshot = localItems.value.filter((item) => !item.deleted);

    const guestUid = useCompareListCookie($locale.value).value[storeKey];
    // Use already-loaded customer data (loadCustomer() was called in onLoggedIn)
    const customerListUid =
      customerAccountStore.customerData?.compare_list?.uid ?? null;

    // Step 1: Handle Magento cookie / assignment
    if (guestUid) {
      if (!customerListUid || guestUid !== customerListUid) {
        try {
          const assigned = await $fetch<
            AssignCompareListToCustomerMutation['assignCompareListToCustomer']
          >('/api/magento/compare/assign', {
            method: 'POST',
            query: {
              store: storeKey,
              locale: $locale.value
            },
            body: { uid: guestUid }
          });

          if (assigned?.compare_list?.uid) {
            const c = useCompareListCookie($locale.value);
            c.value = { ...c.value, [storeKey]: assigned.compare_list.uid };
          } else {
            // Assign returned no UID — fall back to cookie sync
            const c = useCompareListCookie($locale.value);
            c.value = {
              ...c.value,
              [storeKey]: customerListUid || guestUid
            };
          }
        } catch (error) {
          logger.error(
            'CompareListStore',
            'Failed to assign guest list',
            error
          );
          // Fall back: keep whichever UID is available
          const c = useCompareListCookie($locale.value);
          c.value = { ...c.value, [storeKey]: customerListUid || guestUid };
        }
      }
      // If guestUid === customerListUid, lists are already the same — no reassignment needed
    } else {
      // No guest list: sync cookie to customer list if one exists
      if (customerListUid) {
        const c = useCompareListCookie($locale.value);
        c.value = { ...c.value, [storeKey]: customerListUid };
      }
    }

    // Step 2: Fetch the (now merged) customer list from Magento and merge with guest snapshot.
    // activeListUid reads from cookie during isMergingGuestList.
    const mergedUid = activeListUid.value;

    if (mergedUid) {
      // Retry fetching if Magento returns empty due to eventual consistency.
      // This is especially important after assignCompareListToCustomer, as the
      // merged list may not be immediately available.
      const maxFetchRetries = 5;
      const fetchRetryDelayMs = 500;
      let customerList: CompareListData | null = null;

      for (let attempt = 0; attempt <= maxFetchRetries; attempt++) {
        try {
          customerList = await fetchCompareList(mergedUid);

          // If we got items, break out of retry loop
          if (customerList?.items?.length) {
            break;
          }

          // If empty and we have more retries, wait and try again
          if (attempt < maxFetchRetries) {
            await new Promise((resolve) =>
              setTimeout(resolve, fetchRetryDelayMs)
            );
          }
        } catch {
          // On final attempt, let the outer catch handle it
          if (attempt === maxFetchRetries) {
            throw new Error('Failed to fetch compare list');
          }
          // Otherwise wait and retry
          await new Promise((resolve) =>
            setTimeout(resolve, fetchRetryDelayMs)
          );
        }
      }

      if (customerList) {
        const customerItems: CompareItemState[] = (customerList.items ?? [])
          .filter(Boolean)
          .map((item) => ({
            product: item!.product,
            uid: item!.uid,
            attributes: item!.attributes,
            new: false,
            deleted: false,
            deleteSent: false,
            retryCount: 0,
            isEnriching: false
          }));

        const customerProductUids = new Set(
          customerItems.map((i) => i.product.uid)
        );

        // Guest items not yet in Magento: preserve them and mark as new for sync
        const guestOnlyItems: CompareItemState[] = guestSnapshot
          .filter((item) => !customerProductUids.has(item.product.uid))
          .map((item) => ({
            ...item,
            new: true,
            deleted: false,
            retryCount: 0
          }));

        localItems.value = [...customerItems, ...guestOnlyItems];

        if (guestOnlyItems.length > 0) {
          debouncedSync();
        }
      } else {
        // customerList is null after retries — keep guest snapshot
        localItems.value = guestSnapshot.map((item) => ({
          ...item,
          new: true,
          retryCount: 0
        }));
        if (localItems.value.length > 0) {
          debouncedSync();
        }
      }
    } else {
      // No Magento list at all: keep guest snapshot and sync it up
      if (guestSnapshot.length > 0) {
        localItems.value = guestSnapshot.map((item) => ({
          ...item,
          new: true,
          retryCount: 0
        }));
        debouncedSync();
      }
    }
  }

  async function onLoggedIn(): Promise<void> {
    if (!isAuthenticated.value || isMergingGuestList.value) {
      return;
    }

    isMergingGuestList.value = true;
    lastError.value = undefined;

    try {
      // Load customer data first so mergeGuestCompareListAfterLogin can use it.
      // Do NOT clear localItems here — the merge function snapshots and preserves them.
      await customerAccountStore.loadCustomer();

      await mergeGuestCompareListAfterLogin();

      // Retry mechanism for Magento eventual consistency:
      // If localItems is empty after merge, the customer might have a compare list
      // that Magento hasn't fully propagated yet. Wait and retry using refresh().
      const maxRetries = 5;
      const retryDelayMs = 1000;
      let retries = 0;

      while (localItems.value.length === 0 && retries < maxRetries) {
        retries++;
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));

        // Reload customer data — compare_list.uid may now be available
        await customerAccountStore.loadCustomer();

        const customerListUid =
          customerAccountStore.customerData?.compare_list?.uid;
        if (customerListUid) {
          // Sync cookie to customer list UID so refresh() picks it up
          const storeKey = $shopStore.value;
          const c = useCompareListCookie($locale.value);
          c.value = { ...c.value, [storeKey]: customerListUid };

          // Use refresh() to fetch and populate localItems
          await refresh();

          if (localItems.value.length > 0) {
            break; // Success — exit retry loop
          }
        }
      }

      // Reload customer data in case the merge modified the customer's list reference
      await customerAccountStore.loadCustomer();
    } catch (e) {
      lastError.value = extractError(e);
    } finally {
      isMergingGuestList.value = false;
    }
  }

  function onLoggedOut(): void {
    localItems.value = [];
    lastError.value = undefined;

    const storeKey = $shopStore.value;
    const c = useCompareListCookie($locale.value);
    c.value = { ...c.value, [storeKey]: undefined };
  }

  const decoratedProducts = computed(() => {
    const { $textDictionary, $locale } = useNuxtApp();
    return items.value.map((item) => {
      const product = item.product as unknown as CommonProductFragment;

      return DecoratedProduct.build(
        product,
        $textDictionary.value,
        $locale.value
      );
    });
  });

  return {
    compareList: computed(() => ({
      uid: activeListUid.value,
      item_count: itemCount.value,
      items: items.value
    })),
    localItems: computed(() => localItems.value),
    loading,
    lastError,
    syncing: computed(() => syncing.value),
    isMergingGuestList,
    itemCount,
    items,
    activeListUid,
    hasProduct,
    refresh,
    ensureListUid,
    addProduct,
    removeProduct,
    onLoggedIn,
    onLoggedOut,
    mergeGuestCompareListAfterLogin,
    decoratedProducts,
    currentAction,
    lastAction
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCompareListStore, import.meta.hot));
}
